import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import api, { formatApiError } from '../lib/api';
import { images } from '../data/mockData';
import {
  CheckCircle,
  User,
  Users,
  Clock,
  Zap,
  Plus,
  ArrowRight,
  CalendarDays,
  Loader2,
} from 'lucide-react';

const tabs = [
  { id: 'individual', label: 'Individual', icon: User },
  { id: 'couple', label: 'Couple', icon: Users },
  { id: 'add_on', label: 'Add-on', icon: Plus },
  { id: 'flexible', label: 'Flexible Recharge', icon: Zap },
];

const descriptions = {
  individual: 'Perfect for solo swimmers. 1 hour per day access to our non-chlorine pool.',
  couple: 'For two swimmers. 1 hour per day per member in our premium pool.',
  add_on: 'Add an extra kid or adult to a Couple membership. Priced per person, valid for the membership duration.',
  flexible: 'Buy hours, swim anytime. Sharing allowed with 1 member pass.',
};

// "Individual - 6 Months" -> "6 Months"
const planTitle = (name) => (name.includes(' - ') ? name.split(' - ').slice(1).join(' - ') : name);

const MembershipPage = () => {
  const [activeTab, setActiveTab] = useState('individual');
  const [plans, setPlans] = useState({ individual: [], couple: [], add_on: [], flexible: [] });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    api.get('/membership-plans', { params: { active_only: true } })
      .then((res) => {
        const grouped = { individual: [], couple: [], add_on: [], flexible: [] };
        res.data.forEach((p) => {
          if (grouped[p.type]) grouped[p.type].push(p);
        });
        Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a.price - b.price));
        setPlans(grouped);
      })
      .catch((err) => toast({ title: 'Failed to load plans', description: formatApiError(err), variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [toast]);

  const handleSelect = (planName) => {
    toast({ title: 'Plan Selected!', description: `You've selected ${planName}. Our team will contact you shortly.` });
  };

  const currentPlans = plans[activeTab] || [];
  const popularIdx = currentPlans.length === 3 ? 1 : Math.min(2, currentPlans.length - 1);

  const renderPlanCard = (plan, idx) => {
    const isPopular = idx === popularIdx;
    const title = planTitle(plan.name);
    return (
      <Card
        key={plan.id}
        className={`relative border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
          isPopular
            ? 'border-cyan-400 shadow-lg shadow-cyan-100 bg-gradient-to-b from-white to-cyan-50/40'
            : 'border-gray-200 bg-white'
        }`}
        data-testid={`plan-${plan.id}`}
      >
        {isPopular && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-cyan-500 text-white shadow-md px-4">Best Value</Badge>
          </div>
        )}
        <CardContent className="p-6 space-y-5 pt-8">
          <div className="text-center space-y-2">
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl font-extrabold text-cyan-600">{'\u20B9'}{plan.price.toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <CalendarDays className="h-4 w-4 text-cyan-500 flex-shrink-0" />
              <span>Validity: {plan.duration_days} Days</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="h-4 w-4 text-cyan-500 flex-shrink-0" />
              <span>{plan.description || (plan.hours_included ? `${plan.hours_included} Hours included` : '1 hour per day')}</span>
            </div>
          </div>

          <Button
            onClick={() => handleSelect(`${title} - \u20B9${plan.price.toLocaleString('en-IN')}`)}
            className={`w-full ${isPopular ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-gray-900 hover:bg-gray-800'}`}
            size="lg"
            data-testid={`select-plan-${plan.id}`}
          >
            Choose Plan
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative h-64 overflow-hidden">
        <img src={images.familyPool} alt="Swimming" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/80 to-teal-800/70" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-4">
          <div className="space-y-3">
            <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="membership-title">
              Self Swimming Memberships
            </h1>
            <p className="text-lg text-cyan-100 max-w-xl mx-auto">Choose the plan that fits your lifestyle</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-center mb-4">
          <div className="inline-flex bg-white rounded-xl p-1.5 shadow-md border border-gray-100" data-testid="membership-tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === tab.id ? 'bg-cyan-500 text-white shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-center text-gray-500 mb-10" data-testid="tab-description">{descriptions[activeTab]}</p>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="plans-loading">
            <Loader2 className="h-10 w-10 animate-spin text-cyan-500" />
          </div>
        ) : (
          <div
            className={`grid gap-6 ${activeTab === 'flexible' ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4'}`}
            data-testid="plans-grid"
          >
            {currentPlans.map((plan, idx) => renderPlanCard(plan, idx))}
          </div>
        )}

        <div className="mt-16 bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
          <h3 className="text-xl font-bold text-gray-900 text-center mb-8">All Memberships Include</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Non-Chlorine Salt Ionized Pool',
              'UV Water Filtration System',
              'Premium Glass Tile Pool',
              'Locker & Changing Room',
              'Shower Facilities',
              'Top Safety Standards',
              'Towel Service',
              'Flexible Timing',
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-r from-cyan-600 to-teal-600 border-0 shadow-xl">
            <CardContent className="p-10 space-y-4">
              <h2 className="text-2xl font-bold text-white">Need a Custom Plan?</h2>
              <p className="text-cyan-100">Contact us for corporate memberships, group bookings, or custom packages</p>
              <Button asChild size="lg" className="bg-white text-cyan-700 hover:bg-gray-50">
                <a href="/contact">Contact Our Team</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
